import {
  Box,
  DialogActions,
  DialogContent,
  CircularProgress,
  Typography,
  Fade,
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { makeStyles } from '@mui/styles';
import { downloadDocument } from '../../api/DocumentsService';
import CbButton from '../Buttons/CbButton';
import { EmlPreview } from './EmlPreview';
import { isEmlDocument } from './emlDocument';

const DocumentPreviewModal = ({ data, ...props }: any) => {
  const classes = useStyles();
  const { docId, onFetch, fileName } = data;

  const fetchDocSrc = () => {
    return typeof onFetch === 'function' ? onFetch() : defaultFetch(docId);
  };

  const { data: docSrc, isLoading } = useQuery(
    ['doc-preview', docId],
    () => fetchDocSrc(),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      cacheTime: 0, // disable cache as query creates signed src URLs with expiration
    }
  );

  const validSrc =
    typeof docSrc === 'string' &&
    (docSrc.startsWith('http') || docSrc.startsWith('blob:'));

  if (validSrc && isEmlDocument(fileName, docSrc)) {
    return (
      <>
        <DialogContent style={{ padding: '2rem 4rem' }}>
          <EmlPreview
            src={docSrc}
            className={classes.iframe}
            loading={<LoadingPreview />}
            fallback={<Placeholder />}
          />
        </DialogContent>
        <DialogActions>
          <CbButton styleName="ctaButton" onClick={props.close}>
            Done
          </CbButton>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent style={{ padding: '2rem 4rem' }}>
        <Fade in={isLoading} timeout={600}>
          <Box sx={{ display: isLoading ? 'block' : 'none' }}>
            <LoadingPreview />
          </Box>
        </Fade>

        <Fade in={!isLoading && !validSrc} timeout={600}>
          <Box sx={{ display: !isLoading && !validSrc ? 'block' : 'none' }}>
            <Placeholder />
          </Box>
        </Fade>

        <Fade in={!isLoading && validSrc} timeout={600}>
          <Box sx={{ display: !isLoading && validSrc ? 'block' : 'none' }}>
            <iframe
              title="applicationDoc"
              className={classes.iframe}
              src={docSrc || ''}
              allowFullScreen
              // @ts-expect-error TS(2322): Type '{ title: string; className: string; src: any... Remove this comment to see the full error message
              webkitallowfullscreen
              mozallowfullscreen
            />
          </Box>
        </Fade>
      </DialogContent>
      <DialogActions>
        <CbButton
          styleName="ctaButton"
          onClick={props.close}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Done'}
        </CbButton>
      </DialogActions>
    </>
  );
};

export const DocumentPreviewModalConfig = {
  DocumentPreviewModal: {
    component: DocumentPreviewModal,
    config: {
      maxWidth: 'xl',
      title: 'Document Preview',
    },
  },
};

const useStyles = makeStyles(() => ({
  iframe: {
    width: '100%',
    minHeight: '70vh',
    border: 0,
    overflow: 'scroll',
  },
}));

// helpers
const LoadingPreview = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    width="100%"
    minHeight="70vh"
    gap={3}
  >
    <CircularProgress size={60} thickness={4} />
    <Box textAlign="center">
      <Typography variant="h6" color="text.primary" gutterBottom>
        Preparing Document Preview
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we generate your preview...
      </Typography>
    </Box>
  </Box>
);

const Placeholder = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    width="100%"
  >
    <h1>We're sorry, we couldn't locate your document</h1>
    <h3>Please try again or contact a network admin for assistance</h3>
  </Box>
);

const defaultFetch = (docId: any) => {
  return downloadDocument({ docId }).then((resp: any) => resp.data);
};
